import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Check as Tick, RotateCcw } from "lucide-react";
import { Pill, Reveal, WordTitle } from "@/components/site/Reveal";
import { PillButton } from "@/components/site/Kit";
import { Tween } from "@/components/site/Journey";
import ShaderBackground from "@/components/site/ShaderBackground";
import TalkFirst from "@/components/site/TalkFirst";
import { identify, track } from "@/lib/track";
import {
  AUDIT,
  DURATION,
  SMALL,
  FREQUENCY,
  JOB,
  MAX_READINESS,
  READINESS,
  STALL,
  STEPS,
  decode,
  encode,
  hours,
  result,
  summary,
} from "@/lib/check";

const EASE = [0.15, 0.9, 0.34, 0.95];
const COUNT = STEPS.length;
const LETTERS = "ABCDEFG";

function Progress({ step, answers, onJump }) {
  return (
    <div className="flex gap-1.5" role="list" aria-label="Progress">
      {STEPS.map((s, i) => {
        const done = answers[i] !== null;
        return (
          <button
            key={s.q}
            type="button"
            role="listitem"
            disabled={!done && i !== step}
            onClick={() => onJump(i)}
            aria-label={`Question ${i + 1}`}
            aria-current={i === step ? "step" : undefined}
            className="flex-1 py-2 disabled:cursor-default"
          >
            <span className="block h-[3px] overflow-hidden rounded-full bg-white/15">
              <span
                className="block h-full origin-left rounded-full bg-white transition-transform duration-700"
                style={{
                  transform: `scaleX(${done || i === step ? 1 : 0})`,
                  opacity: i === step && !done ? 0.5 : 1,
                }}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

// What the answers add up to so far, shown beside the questions.
function SoFar({ answers }) {
  const [job, frequency, duration, , stall, ...ready] = answers;
  const range =
    frequency !== null && duration !== null ? hours(frequency, duration) : null;
  const scored = ready.filter((a) => a !== null);
  const points = ready.reduce(
    (s, a, i) => s + (a === null ? 0 : READINESS[i].options[a].points),
    0,
  );
  const rows = [
    ["The job", job === null ? null : JOB.options[job].noun],
    ["Runs", frequency === null ? null : FREQUENCY.options[frequency].phrase],
    ["Takes", duration === null ? null : DURATION.options[duration].phrase],
    ["Stalls at", stall === null ? null : STALL.options[stall].leak],
    [
      "Foundations",
      scored.length ? `${points} of ${scored.length * 3} so far` : null,
    ],
  ];
  return (
    <div className="hidden rounded-[28px] bg-white/[0.04] p-7 ring-1 ring-white/10 lg:block">
      <p className="text-[12px] uppercase tracking-[0.1em] text-white/45">
        Hours a month
      </p>
      <p className="nm-display mt-2 text-[72px] font-medium leading-none tracking-[-0.03em]">
        {range ? (
          <>
            <Tween value={range[0]} duration={600} />–
            <Tween value={range[1]} duration={600} />
          </>
        ) : (
          <span className="text-white/20">—</span>
        )}
      </p>
      <dl className="mt-8 space-y-3 border-t border-white/10 pt-5 text-[13px]">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4">
            <dt className="text-white/45">{k}</dt>
            <dd className={`text-right ${v ? "" : "text-white/20"}`}>
              {v || "—"}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-6 text-[12px] text-white/35">
        Worked out from your answers.
      </p>
    </div>
  );
}

// The quieter door: leave an email and hear from us once.
function LeaveEmail({ answers }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const send = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setState("invalid");
      return;
    }
    setState("sending");
    try {
      const r = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "(not given)",
          email,
          topics: ["Check"],
          message: `${summary(answers)} Left an email after the check.`,
          page: `/scorecard?c=${encode(answers)}`,
        }),
      });
      if (r.ok) {
        identify(email);
        track("contact_form_submitted", { form: "check_email" });
      }
      setState(r.ok ? "sent" : "error");
    } catch {
      setState("error");
    }
  };
  if (state === "sent")
    return (
      <p
        role="status"
        data-testid="check-email-sent"
        className="flex items-center gap-2 text-[15px] text-white/85"
      >
        <Tick size={16} /> Received. We will write once.
      </p>
    );
  return (
    <form onSubmit={send} noValidate data-testid="check-email">
      <p className="text-[14px] leading-[1.4] text-white/60">
        Not ready to book? Leave your email. We will write once, with what we
        would look at first in this job.
      </p>
      <div className="mt-3 flex gap-2">
        <input
          type="email"
          aria-label="Work email"
          placeholder="Work email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 min-w-0 flex-1 rounded-full bg-white/10 px-5 text-[14px] text-white outline-none ring-1 ring-white/10 placeholder:text-white/35 focus:ring-white/50"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className="h-11 shrink-0 rounded-full px-5 text-[13px] font-medium text-white ring-1 ring-white/30 transition-colors hover:bg-white/10 disabled:opacity-60"
        >
          {state === "sending" ? "Sending…" : "Leave email"}
        </button>
      </div>
      {state === "invalid" && (
        <p role="alert" className="mt-2 text-[13px] text-red-300">
          Enter a valid email.
        </p>
      )}
      {state === "error" && (
        <p role="alert" className="mt-2 text-[13px] text-red-300">
          Not sent. Write to hello@nomus.work instead.
        </p>
      )}
    </form>
  );
}

// The result lives in its link. This makes it easy to keep, and to send to the
// person who decides.
function Keep({ answers, r }) {
  const [copied, setCopied] = useState(false);
  const link = `https://nomus.work/scorecard?c=${encode(answers)}`;
  const mail = `mailto:?subject=${encodeURIComponent(
    `${r.job}: ${r.hours[0]}–${r.hours[1]} hours a month`,
  )}&body=${encodeURIComponent(
    `${summary(answers)}\n\n${r.verdict.title}\n\nThe full result: ${link}`,
  )}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      track("result_shared", { via: "copy" });
    } catch {
      setCopied(false);
    }
  };
  const quiet =
    "inline-flex h-11 items-center rounded-full px-6 text-[13px] font-medium ring-1 ring-black/15 transition-colors hover:bg-black hover:text-white";
  return (
    <section className="px-5 pt-3 print:hidden" data-testid="check-keep">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-5 rounded-[28px] bg-[#f7f7f7] p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="nm-title text-[24px] leading-[1.1] sm:text-[30px]">
            Keep this result.
          </p>
          <p className="mt-2 max-w-[520px] text-[15px] leading-[1.45] opacity-60">
            It lives in this page's link. Send it to yourself, or to the person
            who decides.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={mail}
            onClick={() => track("result_shared", { via: "email" })}
            className="inline-flex h-11 items-center rounded-full bg-black px-6 text-[13px] font-medium text-white transition-colors hover:bg-[#262626]"
            data-testid="keep-email"
          >
            Email it
          </a>
          <button type="button" onClick={copy} className={quiet}>
            {copied ? "Link copied" : "Copy the link"}
          </button>
          <button
            type="button"
            onClick={() => {
              track("result_shared", { via: "print" });
              window.print();
            }}
            className={quiet}
          >
            Save as PDF
          </button>
        </div>
      </div>
    </section>
  );
}

function Result({ answers, onRestart }) {
  const r = result(answers);
  const reduce = useReducedMotion();
  const contact = `/book?c=${encode(answers)}`;
  const small = `/contact?c=${encode(answers)}&small=1`;
  return (
    <main className="bg-white text-black" data-testid="check-result">
      <section className="relative overflow-hidden bg-black px-5 pb-16 pt-28 text-white sm:pt-32">
        <ShaderBackground dark strength={0.22} lines={12} fadeLeft={0} />
        <div className="relative z-10 mx-auto grid max-w-[1400px] items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Pill white>{r.job}</Pill>
            <p
              className="nm-display mt-6 text-[96px] font-medium leading-[0.9] tracking-[-0.04em] sm:text-[150px]"
              data-testid="check-hours"
            >
              <Tween value={r.hours[0]} duration={reduce ? 0 : 1200} />–
              <Tween value={r.hours[1]} duration={reduce ? 0 : 1200} />
            </p>
            <p className="mt-3 text-[18px] text-white/60">hours a month</p>
            <p className="mt-6 max-w-[420px] text-[15px] leading-[1.4] text-white/55">
              {r.sentence} Worked out from your answers.
            </p>
          </div>
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
              className="nm-title text-[38px] leading-[1] sm:text-[64px]"
              data-testid="check-verdict"
            >
              {r.verdict.title}
            </motion.h1>
            <p className="mt-6 max-w-[620px] text-[19px] leading-[1.35] text-white/80 sm:text-[22px]">
              {r.verdict.text}
            </p>
            {r.gated && (
              <p className="mt-5 max-w-[620px] rounded-[16px] bg-white/10 px-5 py-3 text-[14px] leading-[1.4] text-white/85">
                Shared passwords come first. Nothing gets built until logins are
                fixed.
              </p>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-3 print:hidden">
              {r.verdict.audit ? (
                <>
                  <PillButton to={contact} variant="inverse" size="lg">
                    Book the Audit
                  </PillButton>
                  <span className="mr-2 text-[14px] text-white/60">
                    {AUDIT.price} · {AUDIT.time.toLowerCase()}
                  </span>
                  <TalkFirst location="check_result_top" answers={answers} />
                </>
              ) : (
                <>
                  <PillButton
                    to={small}
                    variant="inverse"
                    size="lg"
                    data-testid="small-cta"
                  >
                    Get the small tool
                  </PillButton>
                  <span className="mr-2 text-[14px] text-white/60">
                    {SMALL.price} · {SMALL.time.toLowerCase()}
                  </span>
                </>
              )}
              {(r.verdict.audit || r.verdict.small) && (
                <button
                  type="button"
                  onClick={onRestart}
                  className="inline-flex h-11 items-center gap-2 px-3 text-[13px] text-white/55 transition-colors hover:text-white"
                >
                  <RotateCcw size={14} /> Check another job
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 pt-16 lg:pt-24">
        <div className="mx-auto grid max-w-[1400px] gap-3 md:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-[28px] bg-[#f7f7f7] p-7 sm:p-9">
              <p className="text-[12px] opacity-45">Where it leaks</p>
              <p className="nm-title mt-3 text-[28px] leading-[1.05] sm:text-[36px]">
                {r.leak}.
              </p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="h-full rounded-[28px] bg-[#f7f7f7] p-7 sm:p-9">
              <p className="text-[12px] opacity-45">Who touches it</p>
              <p className="nm-title mt-3 text-[28px] leading-[1.05] sm:text-[36px]">
                {r.people === 1
                  ? "One person."
                  : r.people >= 7
                    ? "More than six people."
                    : r.people === 3
                      ? "Two or three people."
                      : "Four to six people."}
              </p>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="h-full rounded-[28px] bg-[#f7f7f7] p-7 sm:p-9">
              <p className="text-[12px] opacity-45">
                Foundations · {r.readiness} of {MAX_READINESS}
              </p>
              <div className="mt-4 space-y-2.5">
                {READINESS.map((q, i) => (
                  <div key={q.area} className="flex items-center gap-3">
                    <span className="w-[130px] shrink-0 text-[13px]">
                      {q.area}
                    </span>
                    <span className="flex flex-1 gap-1">
                      {[0, 1, 2].map((s) => (
                        <span
                          key={s}
                          className={`h-2 flex-1 rounded-full ${r.points[i] > s ? "bg-black" : "bg-black/10"}`}
                        />
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Keep answers={answers} r={r} />

      {r.verdict.audit && (
        <>
          <section className="px-5 pt-16 lg:pt-24">
            <div className="mx-auto max-w-[1400px]">
              <Reveal>
                <Pill>What the Audit looks at</Pill>
              </Reveal>
              <div className="mt-6 grid gap-3 md:grid-cols-3">
                {r.look.map((text, i) => (
                  <Reveal key={text} delay={i * 100}>
                    <div className="flex h-full min-h-[220px] flex-col justify-between rounded-[28px] bg-[#f7f7f7] p-7 sm:p-9">
                      <span className="nm-display text-[52px] leading-none tracking-[-0.03em] opacity-25">
                        0{i + 1}
                      </span>
                      <p className="mt-8 text-[19px] leading-[1.25] tracking-[-0.015em] sm:text-[22px]">
                        {text}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <section className="px-3 pb-3 pt-16 sm:px-5 sm:pb-5 lg:pt-24 print:hidden">
            <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[28px] bg-black p-7 text-white sm:p-14">
              <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
              <div className="relative z-10 grid items-end gap-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <Pill white>The first step</Pill>
                  <h2 className="nm-title mt-6 text-[44px] leading-[0.98] sm:text-[80px]">
                    The Audit.
                  </h2>
                  <p className="mt-6 max-w-[560px] text-[19px] leading-[1.35] text-white/80 sm:text-[22px]">
                    We follow this job from the first email to the sign-off. It
                    ends with a written map of the job, a fixed price for the
                    tool, and a first version you watch do one real case. If the
                    answer is no, you keep the map.
                  </p>
                  <p className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[15px] text-white/70">
                    <Link
                      to={`/audit/sample?c=${encode(answers)}`}
                      className="nm-link"
                      data-testid="check-to-sample"
                    >
                      Read a sample Audit
                    </Link>
                    <Link to="/audit" className="nm-link">
                      What happens after you say yes
                    </Link>
                  </p>
                </div>
                <div className="lg:col-span-4 lg:col-start-9">
                  <dl className="grid grid-cols-2 gap-3">
                    <div className="rounded-[18px] bg-white/10 p-5">
                      <dt className="text-[12px] text-white/50">Fee</dt>
                      <dd className="nm-display mt-1 text-[30px] leading-none tracking-[-0.02em]">
                        {AUDIT.price}
                      </dd>
                    </div>
                    <div className="rounded-[18px] bg-white/10 p-5">
                      <dt className="text-[12px] text-white/50">Ends in</dt>
                      <dd className="nm-display mt-1 text-[30px] leading-none tracking-[-0.02em]">
                        {AUDIT.time}
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <PillButton to={contact} variant="inverse" size="lg">
                      Book the Audit
                    </PillButton>
                    <TalkFirst
                      location="check_result_audit"
                      answers={answers}
                    />
                  </div>
                  <div className="mt-8 border-t border-white/15 pt-6">
                    <LeaveEmail answers={answers} />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      {r.verdict.small && (
        <section
          className="px-3 pb-3 pt-16 sm:px-5 sm:pb-5 lg:pt-24 print:hidden"
          data-testid="small-offer"
        >
          <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[28px] bg-black p-7 text-white sm:p-14">
            <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
            <div className="relative z-10 grid items-end gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <Pill white>The right size for this job</Pill>
                <h2 className="nm-title mt-6 text-[44px] leading-[0.98] sm:text-[80px]">
                  A small tool.
                </h2>
                <p className="mt-6 max-w-[560px] text-[19px] leading-[1.35] text-white/80 sm:text-[22px]">
                  One tool for this one job. It reads what the job needs,
                  prepares the work and waits for your approval. You watch it do
                  a real case before you rely on it. If the job turns out to
                  need more, we say so before we build.
                </p>
              </div>
              <div className="lg:col-span-4 lg:col-start-9">
                <dl className="grid grid-cols-2 gap-3">
                  <div className="rounded-[18px] bg-white/10 p-5">
                    <dt className="text-[12px] text-white/50">Fee</dt>
                    <dd className="nm-display mt-1 text-[30px] leading-none tracking-[-0.02em]">
                      {SMALL.price}
                    </dd>
                  </div>
                  <div className="rounded-[18px] bg-white/10 p-5">
                    <dt className="text-[12px] text-white/50">Ready</dt>
                    <dd className="nm-display mt-1 text-[30px] leading-none tracking-[-0.02em]">
                      {SMALL.time}
                    </dd>
                  </div>
                </dl>
                <div className="mt-5">
                  <PillButton to={small} variant="inverse" size="lg">
                    Get the small tool
                  </PillButton>
                </div>
                <div className="mt-8 border-t border-white/15 pt-6">
                  <LeaveEmail answers={answers} />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <p className="px-5 pb-10 pt-8 text-center text-[12px] opacity-40">
        We get your email only if you leave it or write to us.
      </p>
    </main>
  );
}

export default function Check() {
  const [params, setParams] = useSearchParams();
  const reduce = useReducedMotion();
  const fromUrl = decode(params.get("c"));
  const [step, setStep] = useState(fromUrl ? COUNT : -1);
  const [answers, setAnswers] = useState(
    () => fromUrl || Array(COUNT).fill(null),
  );
  const lock = useRef(false);
  const timer = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);

  const pick = useCallback(
    (index, option) => {
      if (lock.current) return;
      lock.current = true;
      const next = answers.map((v, i) => (i === index ? option : v));
      setAnswers(next);
      timer.current = window.setTimeout(
        () => {
          lock.current = false;
          if (index === COUNT - 1) {
            const r = result(next);
            track("scorecard_completed", {
              score: r.readiness,
              band: r.scale === "small" ? "small" : r.band,
              hours_low: r.hours[0],
              hours_high: r.hours[1],
              job: r.job,
              stalls_at: r.leak,
              audit_offered: r.verdict.audit,
              offer: r.verdict.small ? "small_tool" : "audit",
            });
            setParams({ c: encode(next) }, { replace: true });
            window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
          }
          setStep(index + 1);
        },
        reduce ? 0 : 360,
      );
    },
    [answers, reduce, setParams],
  );
  const back = useCallback(() => {
    if (!lock.current) setStep((s) => Math.max(-1, s - 1));
  }, []);

  useEffect(() => {
    if (step < 0 || step >= COUNT) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      const options = STEPS[step].options;
      const byLetter = LETTERS.toLowerCase().indexOf(key);
      const index = byLetter >= 0 ? byLetter : "1234567".indexOf(key);
      if (index >= 0 && index < options.length) {
        e.preventDefault();
        pick(step, index);
      } else if (key === "arrowleft" || key === "backspace") {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, pick, back]);

  const start = () => {
    track("scorecard_started");
    setStep(0);
  };
  const restart = () => {
    setParams({}, { replace: true });
    setAnswers(Array(COUNT).fill(null));
    lock.current = false;
    setStep(-1);
    window.scrollTo({ top: 0 });
  };

  if (step >= COUNT) return <Result answers={answers} onRestart={restart} />;

  const current = STEPS[step];
  return (
    <main className="relative min-h-screen overflow-hidden bg-black text-white">
      <ShaderBackground dark strength={0.22} lines={12} fadeLeft={0} />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col px-5 pb-10 pt-28 sm:pt-32">
        <AnimatePresence mode="wait">
          {step === -1 ? (
            <motion.section
              key="intro"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="grid flex-1 items-center gap-10 lg:grid-cols-12"
              data-testid="check-intro"
            >
              <div className="lg:col-span-7">
                <Pill white>The two-minute check</Pill>
                <WordTitle
                  text="Find the job worth fixing."
                  className="mt-6 max-w-[900px] text-[48px] leading-[0.98] sm:text-[84px] lg:text-[100px]"
                />
                <p className="mt-8 max-w-[540px] text-[19px] leading-[1.3] tracking-[-0.015em] text-white/65 sm:text-[22px]">
                  Ten questions about one job your team repeats. You get the
                  hours it costs each month, where it leaks, and a straight
                  answer on whether it is worth an audit.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <PillButton
                    variant="inverse"
                    size="lg"
                    onClick={start}
                    data-testid="check-start"
                  >
                    Start
                  </PillButton>
                  <span className="text-[13px] text-white/45">
                    No email needed.
                  </span>
                </div>
              </div>
              <ol className="space-y-2 lg:col-span-4 lg:col-start-9">
                {[
                  ["The hours", "What the job costs you each month."],
                  ["The leak", "Where it stalls, in one line."],
                  ["The answer", "Worth an audit, or not."],
                ].map(([t, s], i) => (
                  <li
                    key={t}
                    className="rounded-[20px] bg-white/[0.05] p-5 ring-1 ring-white/10"
                  >
                    <span className="text-[11px] tabular-nums text-white/35">
                      0{i + 1}
                    </span>
                    <p className="nm-title mt-1 text-[24px] leading-none">
                      {t}
                    </p>
                    <p className="mt-2 text-[14px] text-white/55">{s}</p>
                  </li>
                ))}
              </ol>
            </motion.section>
          ) : (
            <motion.section
              key={`q${step}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="grid flex-1 gap-10 lg:grid-cols-12"
              data-testid={`check-q-${step}`}
            >
              <div className="lg:col-span-7">
                <Progress
                  step={step}
                  answers={answers}
                  onJump={(i) => i !== step && !lock.current && setStep(i)}
                />
                <div className="mt-8">
                  <Pill white>
                    {String(step + 1).padStart(2, "0")} / {COUNT} ·{" "}
                    {current.kind === "ready" ? "The foundations" : "The job"}
                  </Pill>
                </div>
                <h1 className="nm-title mt-6 max-w-[760px] text-[34px] leading-[1.02] sm:text-[52px]">
                  {current.q}
                </h1>
                <div className="mt-8 space-y-2.5">
                  {current.options.map((option, i) => {
                    const selected = answers[step] === i;
                    return (
                      <motion.button
                        key={option.label}
                        type="button"
                        onClick={() => pick(step, i)}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.1 + i * 0.05,
                          ease: EASE,
                        }}
                        whileTap={{ scale: 0.985 }}
                        aria-pressed={selected}
                        data-testid={`check-answer-${i}`}
                        className={`group flex w-full items-center gap-4 rounded-[20px] p-4 text-left ring-1 transition-colors duration-300 sm:p-5 ${
                          selected
                            ? "bg-white text-black ring-white"
                            : "bg-white/[0.06] ring-white/10 hover:bg-white/[0.12] hover:ring-white/30"
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-medium transition-colors duration-300 ${
                            selected
                              ? "bg-black text-white"
                              : "bg-white/10 group-hover:bg-white/20"
                          }`}
                        >
                          {LETTERS[i]}
                        </span>
                        <span className="text-[16px] leading-[1.25] tracking-[-0.015em] sm:text-[19px]">
                          {option.label}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
                <div className="mt-8 flex items-center justify-between text-[13px] text-white/45">
                  <button
                    type="button"
                    onClick={back}
                    className="inline-flex items-center gap-2 transition-colors hover:text-white"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <span className="hidden sm:inline">
                    Press a letter or a number to answer
                  </span>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-28">
                  <SoFar answers={answers} />
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
